import { withSentryReactRouterV6Routing } from "@sentry/react";
import { ErrorBoundary } from "react-error-boundary";
import { Provider } from "react-redux";
import { BrowserRouter, Route, Routes, useMatch } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { ThemeProvider } from "styled-components";

import "temporal-polyfill/global";

import { Layout } from "@/components/layout/Layout";
import { FmsReportLayout } from "@/components/layout/fms-report/FmsReportLayout";
import { useUserPages } from "@/components/layout/layout.hooks";
import { Input } from "@/components/ui/Input";
import { Tooltip } from "@/components/ui/Tooltip";
import { addIconSet } from "@/components/ui-deprecated";
import { AuthGuard, AuthProvider } from "@/features/auth";
import { useFmsReportTokenValidity } from "@/hooks/fms-report/useFmsReportTokenValidity";
import { useSendLocationChangeUserEvent } from "@/hooks/user-event/useSendLocationChangeUserEvent";
import { AdministrationPage, FmsReportPage } from "@/pages";
import { DashboardPage } from "@/pages/DashboardPage/DashboardPage";
import { NotFoundPage } from "@/pages/errors/NotFoundPage/NotFoundPage";
import { UnexpectedErrorPage } from "@/pages/errors/UnexpectedErrorPage/UnexpectedErrorPage";
import { fmsReportRoute, routes } from "@/routes";
import { initializeSentry } from "@/services/sentry";
import { persistor, store } from "@/store";
import { GlobalStyles } from "@/styles/global.style";
import { theme } from "@/styles/theme";

import "@/locales/i18n";

import "@datamole/wds-css-theme-lely/lely.theme.css";
// import "@datamole/wds-css-normalize/normalize.module.css"; // breaks some of the other styles
import "@/styles/customStyles.less";
import "@/styles/LelyIcons.css";

addIconSet("lely-icon");

initializeSentry();

const RoutesWithSentry = withSentryReactRouterV6Routing(Routes);

const LoadedAuthenticatedApp = () => {
    const userPages = useUserPages();

    useSendLocationChangeUserEvent();

    return (
        <RoutesWithSentry>
            <Route element={<Layout />}>
                {userPages.map(({ path, Component }) => (
                    <Route key={path} path={path} element={<Component />} />
                ))}
                <Route path={routes.administration.path} element={<AdministrationPage />} />
                {/* Fallback page for the moment */}
                <Route path="" element={<DashboardPage />} />
                <Route path={"*"} element={<NotFoundPage />} />
            </Route>
        </RoutesWithSentry>
    );
};

const FmsReportApp = ({ token }: { token: string }) => {
    const isTokenValid = useFmsReportTokenValidity(token);

    return (
        <RoutesWithSentry>
            <Route element={<FmsReportLayout isTokenValid={isTokenValid} />}>
                <Route path={fmsReportRoute.path} element={<FmsReportPage token={token} />} />
            </Route>
        </RoutesWithSentry>
    );
};

const App = () => {
    const farmScanReportRouteMatch = useMatch(fmsReportRoute.path);

    if (farmScanReportRouteMatch && farmScanReportRouteMatch.params.token) {
        return <FmsReportApp token={farmScanReportRouteMatch.params.token} />;
    }

    return (
        <AuthGuard>
            <LoadedAuthenticatedApp />
        </AuthGuard>
    );
};

const AppWithProviders = () => {
    return (
        <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
                <AuthProvider>
                    <ThemeProvider theme={theme}>
                        <Tooltip.Provider>
                            <ErrorBoundary fallback={<UnexpectedErrorPage />}>
                                {/**
                                 * HOTFIX: To guarantee correct order of loaded WDS components styles,
                                 * we need import 'Input' before 'Combobox' and 'List' before 'Menu'
                                 */}
                                <Input style={{ display: "none" }} />

                                <BrowserRouter>
                                    <GlobalStyles />
                                    <App />
                                </BrowserRouter>
                            </ErrorBoundary>
                        </Tooltip.Provider>
                    </ThemeProvider>
                </AuthProvider>
            </PersistGate>
        </Provider>
    );
};

export { AppWithProviders as App };
